import React, { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Settings, User, BarChart2, Gift, FileText } from 'lucide-react';
import LanguageSelector from '../LanguageSelector';
import NotificationsPopover from './NotificationsPopover';
import BackButton from '../BackButton';
import Logo from '../Logo';

interface LayoutProps {
  children: ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navItems = [
    { name: t('dashboard'), path: '/dashboard', icon: <BarChart2 className="h-5 w-5" /> },
    { name: t('rewards'), path: '/rewards', icon: <Gift className="h-5 w-5" /> },
    { name: t('surveys'), path: '/surveys', icon: <FileText className="h-5 w-5" /> },
    { name: t('settings'), path: '/settings', icon: <Settings className="h-5 w-5" /> }
  ];

  const showBackButton = location.pathname !== '/dashboard';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {showBackButton && (
                <BackButton />
              )}
              <Logo size="medium" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-4">
                <LanguageSelector />
                <div className="relative group">
                  <button className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                    <span className="mr-1">{userName}</span>
                    <User className="h-5 w-5" />
                  </button>
                  <div className="absolute right-0 w-48 mt-2 py-1 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-40">
                    <button
                      onClick={() => navigate('/settings')}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      {t('settings')}
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('signOut')}
                    </button>
                  </div>
                </div>
                <NotificationsPopover />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex items-center px-3 py-2 text-sm font-medium ${
                  location.pathname === item.path
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                }`}
              >
                {item.icon}
                <span className="ml-2">{item.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <div className="sm:hidden bg-white border-t border-gray-200 fixed bottom-0 left-0 right-0 z-10">
        <div className="grid grid-cols-4">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center py-2 ${
                location.pathname === item.path ? 'text-blue-600' : 'text-gray-500'
              }`}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <main className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20 sm:pb-8">
        {title && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
        )}
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center">
          <div className="mb-4 sm:mb-0">
            <Logo size="small" />
          </div>
          <div className="flex space-x-6">
            <a 
              href="https://qonso.crd.co/#legal-information-and-privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t('privacyPolicy')}
            </a>
            <a 
              href="https://qonso.crd.co/#terms-and-conditions-of-use-lg" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t('termsOfService')}
            </a>
            <a 
              href="https://qonso.crd.co/#legal-information-and-privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t('legalNotice')}
            </a>
            <a 
              href="https://formshare.ai/r/LJXDbiI3ha" 
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t('contact')}
            </a>
          </div>
        </div>
      </footer>

      {/* Mobile Footer - Added for mobile devices */}
      <footer className="bg-white border-t border-gray-200 py-4 sm:hidden mt-16 pb-20">
        <div className="px-4 flex flex-col items-center">
          <Logo size="small" className="mb-4" />
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-xs">
            <a 
              href="https://qonso.crd.co/#legal-information-and-privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-500 hover:text-gray-700"
            >
              {t('privacyPolicy')}
            </a>
            <a 
              href="https://qonso.crd.co/#terms-and-conditions-of-use-lg" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-500 hover:text-gray-700"
            >
              {t('termsOfService')}
            </a>
            <a 
              href="https://qonso.crd.co/#legal-information-and-privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-gray-500 hover:text-gray-700"
            >
              {t('legalNotice')}
            </a>
            <a 
              href="https://formshare.ai/r/LJXDbiI3ha" 
              className="text-gray-500 hover:text-gray-700"
            >
              {t('contact')}
            </a>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            {t('allRightsReserved')}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;